import React, { FC, useMemo, useState } from "react";
import { Drawer, Table, Popover, Tag, Select, Input, Button } from "antd";
import {
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Menu, Dropdown } from "antd";
import { ColumnsType } from "antd/es/table";

const { Option } = Select;

interface ShortlistedItem {
  materialName: string;
  supplierID: string;
  supplierName: string;
  awardQty: number;
  reason: string;
  askedQty?: number;
  unitPrice?: number;
  taxPercentage?: number;
}

interface TenderShortlistedDrawerProps {
  visible: boolean;
  onClose: () => void;
  shortlistedData: ShortlistedItem[];
  onRemove: (index: number) => void;
  onEdit: (index: number, updated: ShortlistedItem) => void;
}

const colors = [
  "#173F5F",
  "#20639B",
  "#3CAEA3",
  "#F6D55C",
  "#ED553B",
  "#e81e63",
  "#03a9f4",
  "#cddc39",
  "#9c27b0",
  "#ffeb3b",
];

const TenderShortlistedDrawer: FC<TenderShortlistedDrawerProps> = ({
  visible,
  onClose,
  shortlistedData,
  onRemove,
  onEdit,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{
    awardQty: number;
    reason: string;
  }>({ awardQty: 0, reason: "" });
  const [filterSupplier, setFilterSupplier] = useState<string | undefined>(
    undefined
  );

  const supplierColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    let colorIndex = 0;
    shortlistedData.forEach((item) => {
      if (!map[item.supplierName]) {
        map[item.supplierName] = colors[colorIndex % colors.length];
        colorIndex++;
      }
    });
    return map;
  }, [shortlistedData]);

  const supplierOptions = useMemo(() => {
    const unique = Array.from(
      new Set(shortlistedData.map((item) => item.supplierName))
    );
    return unique.map((name) => ({
      label: name,
      value: name,
    }));
  }, [shortlistedData]);

  const filteredData = filterSupplier
    ? shortlistedData.filter((item) => item.supplierName === filterSupplier)
    : shortlistedData;

  const getSupplierInitials = (name: string) => {
    const words = name?.split(" ");
    return words?.length > 1
      ? `${words[0][0]}${words[1][0]}`.toUpperCase()
      : name?.substring(0, 2).toUpperCase();
  };

  const handleEdit = (idx: number) => {
    const item = shortlistedData[idx];
    setEditingIndex(idx);
    setEditValues({ awardQty: item.awardQty, reason: item.reason });
  };

  const handleSave = () => {
    if (editingIndex !== null) {
      const original = shortlistedData[editingIndex];
      onEdit(editingIndex, {
        ...original,
        awardQty: editValues.awardQty,
        reason: editValues.reason,
      });
      setEditingIndex(null);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Supplier Name",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 180,
      render: (text: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: supplierColorMap[text] || "#1890ff",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {getSupplierInitials(text)}
          </div>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 220,
    },
    {
      title: "Asked Qty",
      dataIndex: "askedQty",
      key: "askedQty",
      width: 100,
      align: "center",
      render: (val: number) => val || "-",
    },
    {
      title: "Awarded Qty",
      dataIndex: "awardQty",
      key: "awardQty",
      width: 120,
      align: "center",
      render: (text: number, _: any, index: number) => {
        const realIndex = shortlistedData.indexOf(filteredData[index]);
        return editingIndex === realIndex ? (
          <Input
            value={editValues.awardQty}
            type="number"
            size="small"
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                awardQty: Number(e.target.value),
              }))
            }
          />
        ) : (
          text
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
      render: (text: string, _: any, index: number) => {
        const realIndex = shortlistedData.indexOf(filteredData[index]);
        return editingIndex === realIndex ? (
          <Input
            value={editValues.reason}
            size="small"
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                reason: e.target.value,
              }))
            }
          />
        ) : (
          text
        );
      },
    },
    {
      title: "Awarded %",
      key: "awardedPercentage",
      width: 100,
      align: "center",
      render: (_: any, record: any) => {
        if (!record.askedQty) return "-";
        const pct = ((record.awardQty / record.askedQty) * 100).toFixed(1);
        return `${pct}%`;
      },
    },
    {
      title: "Unit Price (INR)",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 130,
      align: "right",
      render: (val: number) =>
        val
          ? val.toLocaleString("en-IN", { minimumFractionDigits: 2 })
          : "-",
    },
    {
      title: "Sub Total (INR)",
      key: "subTotal",
      width: 130,
      align: "right",
      render: (_: any, record: any) => {
        if (!record.unitPrice) return "-";
        const sub = record.awardQty * record.unitPrice;
        return sub.toLocaleString("en-IN", { minimumFractionDigits: 2 });
      },
    },
    {
      title: "Taxes",
      key: "taxes",
      width: 120,
      align: "right",
      render: (_: any, record: any) => {
        if (!record.unitPrice || !record.taxPercentage) return "-";
        const base = record.awardQty * record.unitPrice;
        const tax = base * (record.taxPercentage / 100);
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 4,
            }}
          >
            <span>
              {tax.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
            <Popover
              content={
                <div>
                  <div>
                    <b>Tax Type:</b> SST
                  </div>
                  <div>
                    <b>Tax Rate:</b> {record.taxPercentage}%
                  </div>
                  <div>
                    <b>Base:</b>{" "}
                    {base.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              }
              title="Tax Breakdown"
            >
              <InfoCircleOutlined
                style={{ color: "#1890ff", cursor: "pointer" }}
              />
            </Popover>
          </div>
        );
      },
    },
    {
      title: "Line Total (INR)",
      key: "lineTotal",
      width: 140,
      align: "right",
      render: (_: any, record: any) => {
        if (!record.unitPrice) return "-";
        const base = record.awardQty * record.unitPrice;
        const tax = base * ((record.taxPercentage || 0) / 100);
        return (base + tax).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        });
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      align: "center",
      render: (_: any, __: any, index: number) => {
        const realIndex = shortlistedData.indexOf(filteredData[index]);
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(realIndex)}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => onRemove(realIndex)}
                >
                  Remove
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: 16 }} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Drawer
      title="Shortlisted Suppliers"
      width="95vw"
      onClose={onClose}
      open={visible}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Filter by:</span>
          <Select
            placeholder="Select Supplier"
            style={{ width: 200 }}
            value={filterSupplier}
            onChange={(val) => setFilterSupplier(val)}
            allowClear
          >
            {supplierOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
        {editingIndex !== null && (
          <div>
            <Button
              type="primary"
              size="small"
              onClick={handleSave}
              style={{ marginRight: 8 }}
            >
              Save
            </Button>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredData.map((item, idx) => ({
          ...item,
          key: idx,
        }))}
        size="small"
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
      />
    </Drawer>
  );
};

export default TenderShortlistedDrawer;
